import { createPlayerBuilder as createAdsPlayerBuilder } from 'fl-ads-ima';
import {
  ConsumptionType,
  ContentAuthorizationData,
  ContentAuthorizer,
  PlatformAsset,
  PlaybackMode,
  RenewLicenseAsset,
} from 'fl-content-authorization';
import { AuthorizationUtil, PlatformAuthorizer } from 'fl-platform-authorizer';
import { getLicenseHandler } from 'fl-platform-player';
import {
  PlayerBuilder,
  ThumbnailPreviewConfig,
  createComposablePlayer,
  createPlayerBuilder,
} from 'fl-player';
import {
  AdInfo,
  AdPlayer,
  ComposablePlayer,
  DrmScheme,
  MediaType,
  PlaybackOptions,
  Player,
  RequestType,
  TrackType,
} from 'fl-player-interface';
import { ResourceVm } from 'qp-ui-interface';
import { applicationConstants } from '../../shared/applicationConstants';
import { IPlayer } from '../common/interfaces/common.interface';
import { LocalStorageService } from '../services/storage/local-storage.service';
import { GetPlatformPlayerModel } from './../interfaces/player.interface';
import { AppConfig } from './constants/app-config.constant';
import { KEYS } from './constants/storage.constants';
import { OFF } from './deviceUtils';

declare let navigator: any;
const LICENSE_RENEW_THRESHOLD_IN_SEC = 60;

const getCatalogType = (metadataType: string): string => {
  switch (metadataType) {
    case 'webseries':
    case 'webepisode':
      return 'webepisode';
    default:
      return metadataType;
  }
};

const getConsumptionType = (metadataType: string): ConsumptionType => {
  switch (metadataType) {
    case 'channel':
    case 'livechannel':
    case 'liveevent':
      return ConsumptionType.LIVE;
    default:
      return ConsumptionType.VOD;
  }
};

const getPlaybackMode = (metadataType: string): PlaybackMode | undefined => {
  switch (metadataType) {
    case 'channel':
    case 'livechannel':
    case 'liveevent':
      return PlaybackMode.LIVE;
    default:
      return undefined;
  }
};

export const getPlatformAsset = (
  contentId: string,
  metadataType: string
): PlatformAsset => {
  let model = '';
  if (navigator.userAgent.includes('Tizen')) {
    const arr = navigator.userAgent.split(' ');
    model = arr[1] + ' ' + arr[2] + ' ' + arr[3] + ' ' + arr[4] + ' ';
  } else if (navigator.userAgent.includes('Web0S')) {
    const arr = navigator.userAgent.split(' ');
    model = arr[1] + ' ' + arr[2];
  }
  const platformAsset: PlatformAsset = {
    mediaID: contentId,
    consumptionType: getConsumptionType(metadataType),
    catalogType: getCatalogType(metadataType),
    mediaType: MediaType.DASH,
    drmScheme: getDrmScheme(),
    deviceModelNumber: model,
    supportedAudio: window.xSupportedAudio?.join(','),
    custom: {
      'pf.multiaudiocodecsupport': 'true',
      'pf.multiaudiolangsupport': 'true',
    },
  };

  const playbackMode = getPlaybackMode(metadataType);
  if (playbackMode) {
    platformAsset.playbackMode = playbackMode;
  }

  return platformAsset;
};

export const getDrmScheme = (): DrmScheme => {
  let drmScheme = DrmScheme.WIDEVINE;
  sessionStorage.setItem(KEYS.DRM_NAME, drmScheme);
  return drmScheme;
};

const getBufferConfig = (applicationConfig: AppConfig) => {
  const playbackBufferOptions: Pick<
    PlaybackOptions,
    'minBuffer' | 'maxBuffer' | 'bufferBehind' | 'ignoreMinBufferTime'
  > = {};

  if (applicationConfig.minBuffer) {
    playbackBufferOptions.minBuffer = parseFloat(applicationConfig.minBuffer);
  }

  if (applicationConfig.maxBuffer) {
    playbackBufferOptions.maxBuffer = parseFloat(applicationConfig.maxBuffer);
  }

  if (applicationConfig.bufferBehind) {
    playbackBufferOptions.bufferBehind = parseFloat(
      applicationConfig.bufferBehind
    );
  }

  if (applicationConfig.ignoreMinBufferTime) {
    playbackBufferOptions.ignoreMinBufferTime =
      applicationConfig.ignoreMinBufferTime === 'true';
  }

  return playbackBufferOptions;
};

const getNetworkConfig = (applicationConfig: AppConfig) => {
  const playbackNetworkConfig: Pick<
    PlaybackOptions,
    | 'networkRetries'
    | 'networkTimeout'
    | 'connectionTimeout'
    | 'useNetworkInformation'
  > = {};

  if (applicationConfig.networkRetries) {
    playbackNetworkConfig.networkRetries = parseFloat(
      applicationConfig.networkRetries
    );
  }

  if (applicationConfig.networkTimeout) {
    playbackNetworkConfig.networkTimeout = parseFloat(
      applicationConfig.networkTimeout
    );
  }

  if (applicationConfig.connectionTimeout) {
    playbackNetworkConfig.connectionTimeout = parseFloat(
      applicationConfig.connectionTimeout
    );
  }

  if (applicationConfig.useNetworkInformation) {
    playbackNetworkConfig.useNetworkInformation =
      applicationConfig.useNetworkInformation === 'true';
  }

  return playbackNetworkConfig;
};

const getVodPlaybackOptions = (
  applicationConfig: AppConfig
): PlaybackOptions => {
  // set default audio codec as 'eac3-joc'
  const getUserPeferredAudioCodecs =
    localStorage.getItem('preferedAudioQuality') ??
    applicationConfig.userPeferredAudioCodecs ??
    applicationConstants.EAC3_JOC; // UIUX-6036

  const playbackOptions: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    segmentPrefetchLimit: Number(applicationConfig.segmentPrefetchLimit) || 1, // UIUX-6441
    enablePreload: true, // UIUX-6441
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    // autoPlayManagedByPlatform: true,
    preferredAudioCodecs: [getUserPeferredAudioCodecs], // UIUX-6036
    ...getBufferConfig(applicationConfig),
    ...getNetworkConfig(applicationConfig),
  };

  if (applicationConfig.stallTimeout) {
    playbackOptions.stallTimeout = parseFloat(applicationConfig.stallTimeout);
  }

  if (applicationConfig.connectionTimeout) {
    playbackOptions.connectionTimeout = parseFloat(
      applicationConfig.connectionTimeout
    );
  }

  if (applicationConfig.retryDelay) {
    playbackOptions.retryDelay = parseFloat(applicationConfig.retryDelay);
  }

  if (applicationConfig.preferredBitrate) {
    playbackOptions.preferredBitrate = parseFloat(
      applicationConfig.preferredBitrate
    );
  }

  if (applicationConfig.inaccurateManifestTolerance) {
    playbackOptions.inaccurateManifestTolerance = parseFloat(
      applicationConfig.inaccurateManifestTolerance
    );
  }

  if (applicationConfig.liveIgnoreSuggestedPresentationDelay) {
    playbackOptions.ignoreSuggestedPresentationDelay =
      applicationConfig.liveIgnoreSuggestedPresentationDelay === 'true';
  }

  if (applicationConfig.liveDefaultPresentationDelay) {
    playbackOptions.defaultPresentationDelay = parseFloat(
      applicationConfig.liveDefaultPresentationDelay
    );
  }

  if (applicationConfig.liveMinTotalBytes) {
    playbackOptions.minTotalBytes = parseFloat(
      applicationConfig.liveMinTotalBytes
    );
  }

  if (applicationConfig.minBytes) {
    playbackOptions.minBytes = parseFloat(applicationConfig.minBytes);
  }

  if (applicationConfig.fastHalfLife) {
    playbackOptions.fastHalfLife = parseFloat(applicationConfig.fastHalfLife);
  }

  if (applicationConfig.slowHalfLife) {
    playbackOptions.slowHalfLife = parseFloat(applicationConfig.slowHalfLife);
  }

  if (applicationConfig.bandwidthUpgradeTarget) {
    playbackOptions.bandwidthUpgradeTarget = parseFloat(
      applicationConfig.bandwidthUpgradeTarget
    );
  }

  if (applicationConfig.bandwidthDowngradeTarget) {
    playbackOptions.bandwidthDowngradeTarget = parseFloat(
      applicationConfig.bandwidthDowngradeTarget
    );
  }

  if (applicationConfig.switchInterval) {
    playbackOptions.switchInterval = parseFloat(
      applicationConfig.switchInterval
    );
  }

  return playbackOptions;
};

const getLiveBufferOptionsConfig = (applicationConfig: AppConfig) => {
  const playbackBufferOptions: Pick<
    PlaybackOptions,
    'minBuffer' | 'maxBuffer' | 'bufferBehind' | 'ignoreMinBufferTime'
  > = {};

  if (applicationConfig.liveMinBuffer) {
    playbackBufferOptions.minBuffer = parseFloat(
      applicationConfig.liveMinBuffer
    );
  }

  if (applicationConfig.liveMaxBuffer) {
    playbackBufferOptions.maxBuffer = parseFloat(
      applicationConfig.liveMaxBuffer
    );
  }

  if (applicationConfig.liveBufferBehind) {
    playbackBufferOptions.bufferBehind = parseFloat(
      applicationConfig.liveBufferBehind
    );
  }

  if (applicationConfig.liveIgnoreMinBufferTime) {
    playbackBufferOptions.ignoreMinBufferTime =
      applicationConfig.liveIgnoreMinBufferTime === 'true';
  }
  return playbackBufferOptions;
};

const getLiveNetworkConfig = (applicationConfig: AppConfig) => {
  const playbackNetworkConfig: Pick<
    PlaybackOptions,
    | 'networkRetries'
    | 'networkTimeout'
    | 'connectionTimeout'
    | 'useNetworkInformation'
  > = {};

  if (applicationConfig.liveNetworkRetries) {
    playbackNetworkConfig.networkRetries = parseFloat(
      applicationConfig.liveNetworkRetries
    );
  }

  if (applicationConfig.liveNetworkTimeout) {
    playbackNetworkConfig.networkTimeout = parseFloat(
      applicationConfig.liveNetworkTimeout
    );
  }

  if (applicationConfig.liveConnectionTimeout) {
    playbackNetworkConfig.connectionTimeout = parseFloat(
      applicationConfig.liveConnectionTimeout
    );
  }

  if (applicationConfig.liveUseNetworkInformation) {
    playbackNetworkConfig.useNetworkInformation =
      applicationConfig.liveUseNetworkInformation === 'true';
  }
  return playbackNetworkConfig;
};

const getLivePlaybackOptions = (
  applicationConfig: AppConfig
): PlaybackOptions => {
  // set default audio codec as 'eac3-joc'
  const getUserPeferredAudioCodecs =
    localStorage.getItem('preferedAudioQuality') ??
    applicationConfig.userPeferredAudioCodecs ??
    'eac3-joc'; // UIUX-6036
  const playbackOptions: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    preferredAudioCodecs: [getUserPeferredAudioCodecs], // UIUX-6036
    ...getLiveBufferOptionsConfig(applicationConfig),
    ...getLiveNetworkConfig(applicationConfig),
  };

  if (applicationConfig.liveStallTimeout) {
    playbackOptions.stallTimeout = parseFloat(
      applicationConfig.liveStallTimeout
    );
  }

  if (applicationConfig.liveRetryDelay) {
    playbackOptions.retryDelay = parseFloat(applicationConfig.liveRetryDelay);
  }

  if (applicationConfig.livePreferredBitrate) {
    playbackOptions.preferredBitrate = parseFloat(
      applicationConfig.livePreferredBitrate
    );
  }

  if (applicationConfig.liveInaccurateManifestTolerance) {
    playbackOptions.inaccurateManifestTolerance = parseFloat(
      applicationConfig.liveInaccurateManifestTolerance
    );
  }

  if (applicationConfig.liveIgnoreSuggestedPresentationDelay) {
    playbackOptions.ignoreSuggestedPresentationDelay =
      applicationConfig.liveIgnoreSuggestedPresentationDelay === 'true';
  }

  if (applicationConfig.liveDefaultPresentationDelay) {
    playbackOptions.defaultPresentationDelay = parseFloat(
      applicationConfig.liveDefaultPresentationDelay
    );
  }

  if (applicationConfig.liveMinTotalBytes) {
    playbackOptions.minTotalBytes = parseFloat(
      applicationConfig.liveMinTotalBytes
    );
  }

  if (applicationConfig.liveMinBytes) {
    playbackOptions.minBytes = parseFloat(applicationConfig.liveMinBytes);
  }

  if (applicationConfig.liveFastHalfLife) {
    playbackOptions.fastHalfLife = parseFloat(
      applicationConfig.liveFastHalfLife
    );
  }

  if (applicationConfig.liveSlowHalfLife) {
    playbackOptions.slowHalfLife = parseFloat(
      applicationConfig.liveSlowHalfLife
    );
  }

  if (applicationConfig.liveMinBytes) {
    playbackOptions.minBytes = parseFloat(applicationConfig.liveMinBytes);
  }

  if (applicationConfig.liveBandwidthUpgradeTarget) {
    playbackOptions.bandwidthUpgradeTarget = parseFloat(
      applicationConfig.liveBandwidthUpgradeTarget
    );
  }

  if (applicationConfig.liveBandwidthDowngradeTarget) {
    playbackOptions.bandwidthDowngradeTarget = parseFloat(
      applicationConfig.liveBandwidthDowngradeTarget
    );
  }

  if (applicationConfig.liveSwitchInterval) {
    playbackOptions.switchInterval = parseFloat(
      applicationConfig.liveSwitchInterval
    );
  }
  return playbackOptions;
};

const getPlaybackOptions = (
  applicationConfig: AppConfig,
  initialPlaybackTime = 0,
  isLive = false,
  resource: ResourceVm,
  isLoggedIn: boolean
): PlaybackOptions => {
  const navigatorAgent = navigator.userAgent;
  const chrome = navigatorAgent
    .substr(navigatorAgent.indexOf('Chrome/'), navigatorAgent.indexOf('.'))
    .split('/');
  let playbackOptions: PlaybackOptions;
  if (!isLive) {
    playbackOptions = getVodPlaybackOptions(applicationConfig);
  } else if (isLive) {
    playbackOptions = getLivePlaybackOptions(applicationConfig);
    return playbackOptions;
  }
  // UIUX-6036
  const storedChangeAudio = localStorage.getItem(KEYS.CHANGE_AUDIO);
  if (storedChangeAudio?.length && playbackOptions) {
    const changeAudio = JSON.parse(localStorage.getItem(KEYS.CHANGE_AUDIO));
    playbackOptions.initialPlaybackTime = changeAudio.currentTime;
    playbackOptions.preferredAudioCodecs = [changeAudio.currentAudioQuality];
    localStorage.removeItem(KEYS.CHANGE_AUDIO);
  } else if (playbackOptions) {
    playbackOptions.initialPlaybackTime = initialPlaybackTime;
  }
  // playbackOptions.preferredTextLanguage = 'en';
  playbackOptions.preferredAudioLanguage = getPreferredAudioLanguage(
    resource,
    isLoggedIn
  );

  if (
    (parseInt(chrome[1], 10) < 40 ||
      (!navigatorAgent.includes('Web0S') &&
        !navigatorAgent.includes('Tizen'))) &&
    playbackOptions
  ) {
    // For stop buffering
    playbackOptions.minBuffer = 2;
    playbackOptions.maxBuffer = 3;
    playbackOptions.bufferBehind = 2;
    playbackOptions.enablePreload = true;
    playbackOptions.segmentPrefetchLimit =
      Number(applicationConfig.segmentPrefetchLimit) || 1;
    return playbackOptions;
  }

  return playbackOptions as PlaybackOptions;
};

export const getPreferredAudioLanguage = (
  resource: ResourceVm,
  isLoggedIn: boolean
): string => {
  const aclML = resource.multiLingualAudio;
  if (aclML?.length > 1) {
    const prefLang = isLoggedIn
      ? localStorage.getItem(KEYS.PREFERRED_LANGUAGES) ||
        localStorage.getItem(KEYS.DL_PREFERRED_LANGUAGES)
      : localStorage.getItem(KEYS.ANONYMOUS_PREFERRED_LANGUAGES);
    if (prefLang && prefLang.split(',').length > 0) {
      if (prefLang.split(',').length > 1) {
        return resource.inLanguage;
      }
      return prefLang;
    }
  }
  return resource.inLanguage;
};

const getLanguageName = (code: string): string => {
  switch (code) {
    case 'en': {
      return 'English';
    }
    case 'fr': {
      return 'French';
    }
    case 'es': {
      return 'Spanish';
    }
    case 'de': {
      return 'German';
    }
    case 'it': {
      return 'Italian';
    }
    case 'el': {
      return 'Greek';
    }
    case 'pt-BR': {
      return 'Portuguese';
    }
    default: {
      return getLanguageString(code);
    }
  }
};

export const getTextTrackList = (playerInstance: Player): IPlayer[] => {
  let textTrackList: IPlayer[] = [];
  if (playerInstance) {
    const subtitle = JSON.parse(localStorage.getItem(KEYS.SUBTITLE) || '{}');
    const trackList = playerInstance.getAllTracks();
    /* const activeTracks = playerInstance
      .getActiveTracks()
      .filter((trackList) => {
        return trackList.type === TrackType.TEXT;
      }); */
    const activeTracks =
      trackList?.filter((track) => {
        return (
          track.type === TrackType.TEXT && track.language === subtitle?.value
        );
      }) || [];

    textTrackList = trackList
      .filter((t) => t.type === TrackType.TEXT)
      .map((t, index: number) => ({
        key: index + 1,
        // type: t.type,
        value: t.language,
        name: getLanguageName(t.language),
        isSelected:
          activeTracks.filter((a) => a.type === TrackType.TEXT).length > 0
            ? activeTracks.filter((a) => a.type === TrackType.TEXT)[0]
                .language === t.language
            : false,
      }));

    const offTextTrack: IPlayer = {
      key: textTrackList.length + 1,
      // type: TrackType.TEXT,
      value: OFF.toLowerCase(),
      name: OFF,
      isSelected: activeTracks?.length <= 0,
    };

    textTrackList.unshift(offTextTrack);
  }
  return textTrackList;
};

export const getComposablePlayer = (
  playerInstance: Player
): ComposablePlayer => {
  return createComposablePlayer(playerInstance);
};

const getWidevinePlayer = (
  playbackOptions: PlaybackOptions,
  contentAuthorizationData: ContentAuthorizationData,
  video: HTMLVideoElement,
  videoContainer: HTMLElement,
  thumbnailPreviewEndpointUrl: any,
  thumbnailConfig: any,
  showThumbnails = false
): Promise<Player> => {
  try {
    return getPlayerData(
      playbackOptions,
      contentAuthorizationData,
      video,
      videoContainer,
      thumbnailPreviewEndpointUrl,
      thumbnailConfig,
      showThumbnails
    );
  } catch (_) {
    return playerBuilderData(
      playbackOptions,
      contentAuthorizationData,
      video,
      videoContainer
    );
  }
};

export const getPlatformPlayer = (
  getplatformPlayerModel: GetPlatformPlayerModel,
  isLoggedIn: boolean
): Promise<Player> => {
  const {
    seekPos,
    applicationConfig,
    isLive,
    contentAuthorizationData,
    video,
    videoContainer,
    certificate,
    showThumbnails,
    resource,
  } = getplatformPlayerModel;
  const playbackOptions: PlaybackOptions = getPlaybackOptions(
    applicationConfig,
    seekPos,
    isLive,
    resource,
    isLoggedIn
  );
  try {
    let thumbnailPreviewEndpointUrl: any;
    let thumbnailConfig: any;
    if (showThumbnails) {
      thumbnailPreviewEndpointUrl = getThumbnailPreviewEndpointUrl(
        contentAuthorizationData,
        applicationConfig
      );
      thumbnailConfig = getThumbnailConfig(contentAuthorizationData);
    }

    return getWidevinePlayer(
      playbackOptions,
      contentAuthorizationData,
      video,
      videoContainer,
      thumbnailPreviewEndpointUrl,
      thumbnailConfig,
      showThumbnails
    );
  } catch (e) {
    console.warn('player created on catch', e);
    return getWidevinePlayer(
      playbackOptions,
      contentAuthorizationData,
      video,
      videoContainer,
      undefined,
      undefined,
      undefined
    );
  }
};

export const handleLicenseRequest = async (
  platformAuthorizer: PlatformAuthorizer,
  contentAuthorizer: ContentAuthorizer,
  playerInstance: Player,
  authorizationData: ContentAuthorizationData,
  platformAsset: PlatformAsset
): Promise<Player> => {
  /* const licenseHandler = await getLicenseHandler(platformAuthorizer);
  const requestInterceptor = async (type: any, request: any) => {
    if (type === RequestType.LICENSE) {
      licenseHandler.licenseRequestHandler(request);
      //   await processLicenseRequest(
      //  licenseHandler,
      //   request,
      //   platformAsset,
      //   authorizationData,
      //   contentAuthorizer
      // );
    }
  };

  playerInstance.setRequestInterceptor(requestInterceptor);
  return playerInstance; */

  try {
    /**
     * Get the default license handler from FLPlatformPlayer
     * and set those as request/response interceptors on the player instance.
     *
     * The license handler injects the authorization tokens required for
     * DRM proxy server.
     */
    const licenseHandler = await getLicenseHandler(platformAuthorizer);
    const requestInterceptor = async (
      type: any,
      request: any
    ): Promise<void> => {
      if (type === RequestType.LICENSE) {
        await processLicenseRequest(
          licenseHandler,
          request,
          platformAsset,
          authorizationData,
          contentAuthorizer
        );
      }
    };

    playerInstance.setRequestInterceptor(requestInterceptor);
    return playerInstance;
  } catch (error) {
    console.error('Error handling license request:', error);
    throw error;
  }
};

const processLicenseRequest = async (
  licenseHandler: any,
  request: any,
  platformAsset: PlatformAsset,
  currentPlaybackAsset: ContentAuthorizationData,
  contentAuthorizer: ContentAuthorizer
): Promise<void> => {
  return new Promise<void>((renewResolve) => {
    if (!currentPlaybackAsset) {
      licenseHandler.licenseRequestHandler(request);
      renewResolve();
      return;
    }

    const issuedAt = currentPlaybackAsset.issuedAt;
    const serverLatchTime = getServerTime();
    const elapsedTime = (serverLatchTime - issuedAt) / 1000;

    const shouldRenew = shouldRenewLicense(currentPlaybackAsset, elapsedTime);

    if (shouldRenew) {
      renewLicense(platformAsset, currentPlaybackAsset, contentAuthorizer)
        .then((renewedData: any) => {
          if (request.uris && request.uris.length > 0) {
            request.uris[0] = renewedData;
          }
          // Update the current playback asset with renewed data
          currentPlaybackAsset = renewedData.playbackAsset;
        })
        .catch((error) => {
          console.error('Error renewing license:', error);
        })
        .finally(() => {
          licenseHandler.licenseRequestHandler(request);
          renewResolve();
        });
    } else {
      licenseHandler.licenseRequestHandler(request);
      renewResolve();
    }
  });
};

/**
 * Get the current server time from platform authorizer
 */
const getServerTime = (): number => {
  return AuthorizationUtil?.platformServiceTime() || Date.now();
};

/**
 * Check if license should be renewed based on elapsed time and refresh token
 */
const shouldRenewLicense = (
  playbackAsset: ContentAuthorizationData,
  elapsedTime: number
): boolean => {
  return !!(
    playbackAsset.licenseRefreshToken &&
    elapsedTime >= LICENSE_RENEW_THRESHOLD_IN_SEC
  );
};

/**
 * Renew license URL
 */
const renewLicenseUrl = async (
  platformAsset: PlatformAsset,
  currentPlaybackAsset: ContentAuthorizationData,
  contentAuthorizer: ContentAuthorizer
): Promise<string> => {
  if (!contentAuthorizer) {
    throw new Error('Content authorizer not set');
  }

  const renewedPlaybackAsset: RenewLicenseAsset = {
    mediaID: platformAsset.mediaID,
    drmScheme: platformAsset.drmScheme,
    licenseUrl: currentPlaybackAsset.drmLicenseUrl,
    licenseRefreshToken: currentPlaybackAsset.licenseRefreshToken,
  };

  try {
    const licenseUrl =
      await contentAuthorizer.renewLicense(renewedPlaybackAsset);
    if (licenseUrl instanceof Error) {
      throw licenseUrl;
    }
    return licenseUrl;
  } catch (error) {
    console.error('Failed to renew license URL:', error);
    throw error;
  }
};

/**
 * Renew license and return updated playback asset
 */
const renewLicense = async (
  platformAsset: PlatformAsset,
  currentPlaybackAsset: ContentAuthorizationData,
  contentAuthorizer: ContentAuthorizer
): Promise<string> => {
  try {
    const licenseUrl = await renewLicenseUrl(
      platformAsset,
      currentPlaybackAsset,
      contentAuthorizer
    );

    return licenseUrl;
  } catch (error) {
    console.error('Failed to renew license:', error);
    throw error;
  }
};

const getThumbnailConfig = (
  contentAuthorizationData: ContentAuthorizationData
): ThumbnailPreviewConfig => {
  return {
    numRows: contentAuthorizationData?.keyframe?.rows,
    numColumns: contentAuthorizationData?.keyframe?.columns,
    keyframeDuration: contentAuthorizationData?.keyframe?.frequency,
    // thumbnailWidth: contentAuthorizationData?.keyframe?.width,
    // thumbnailHeight: contentAuthorizationData?.keyframe?.height,
    // totalSpriteIndices: contentAuthorizationData?.keyframe?.ia.length,
  };
};

const getThumbnailPreviewEndpointUrl = (
  contentAuthorizationData: ContentAuthorizationData,
  appConfig: AppConfig
): string => {
  const imageResizeURL = appConfig.imageResizeURL;
  const keyframe = contentAuthorizationData?.keyframe;
  const spriteUrlTemplate = keyframe.ia[0]
    .split('-')
    .slice(0, -1)
    .concat('~index~.jpg')
    .join('-');
  return `${imageResizeURL}/image/${
    contentAuthorizationData.contentId
  }/${spriteUrlTemplate}?width=${keyframe.width * keyframe.columns}&height=${
    keyframe.height * keyframe.rows
  }`;
};

export const getLanguageString = (lang: string): string => {
  lang = lang?.toLowerCase();
  switch (lang) {
    case 'en':
      return 'English';
    case 'tg':
      return 'Telugu';
    case 'tm':
      return 'Tamil';
    case 'te':
      return 'Telugu';
    case 'ta':
      return 'Tamil';
    case 'tamil':
      return 'Tamil';
    case 'telugu':
      return 'Telugu';
    default:
      return 'English';
  }
};

const getPlayerData = (
  playbackOptions: PlaybackOptions,
  contentAuthorizationData: ContentAuthorizationData,
  video: HTMLVideoElement,
  videoContainer: HTMLElement,
  thumbnailPreviewEndpointUrl: any,
  thumbnailConfig: any,
  showThumbnails = false
): Promise<Player> => {
  const playerBuilder: PlayerBuilder = createPlayerBuilder();

  if (showThumbnails) {
    return Promise.resolve(
      playerBuilder
        .mediaElement(video)
        .mediaWrapperElement(videoContainer)
        .mediaUrl(contentAuthorizationData.contentUrl)
        .playbackOptions(playbackOptions)
        .enableThumbnailPreview(thumbnailPreviewEndpointUrl, thumbnailConfig)
        .drmLicenseUrl(contentAuthorizationData.drmLicenseUrl)
        .drmScheme(contentAuthorizationData.drmScheme)
        .mediaType(contentAuthorizationData.mediaType)
        .build()
    );
  }
  return playerBuilderData(
    playbackOptions,
    contentAuthorizationData,
    video,
    videoContainer
  );
};

const playerBuilderData = (
  playbackOptions: PlaybackOptions,
  contentAuthorizationData: ContentAuthorizationData,
  video: HTMLVideoElement,
  videoContainer: HTMLElement
) => {
  const playerBuilder: PlayerBuilder = createPlayerBuilder();
  return Promise.resolve(
    playerBuilder
      .mediaElement(video)
      .mediaWrapperElement(videoContainer)
      .mediaUrl(contentAuthorizationData.contentUrl)
      .playbackOptions(playbackOptions)
      .drmLicenseUrl(contentAuthorizationData.drmLicenseUrl)
      .drmScheme(contentAuthorizationData.drmScheme)
      .mediaType(contentAuthorizationData.mediaType)
      .build()
  );
};

export const getHeaders = (
  currentResource: ResourceVm,
  localStorage: LocalStorageService
): any => {
  if (!currentResource?.isTvod) {
    return null;
  }
  if (localStorage.getItem('tvodToken')) {
    const TVOD_AUTH_HEADER = 'X-Tvod-Authorization';
    return {
      [TVOD_AUTH_HEADER]:
        JSON.parse(localStorage.getItem('tvodToken') || '') || '',
    };
  }
  return null;
};

export const getAutoPreviewPlayer = (
  contentUrl: string,
  video: HTMLVideoElement,
  mediaType = 'dash'
): Promise<Player> => {
  const playerBuilder: PlayerBuilder = createPlayerBuilder();
  let playbackOptions: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
  };
  try {
    return Promise.resolve(
      playerBuilder
        .mediaElement(video)
        .mediaUrl(contentUrl)
        .mediaType(mediaType as MediaType)
        .playbackOptions(playbackOptions)
        .build()
    );
  } catch (_) {
    return Promise.resolve(
      playerBuilder
        .mediaElement(video)
        .mediaUrl(contentUrl)
        .mediaType(mediaType as MediaType)
        .build()
    );
  }
};

export const getAdPlayer = (
  playerInstance: Player,
  adTagUrl: string,
  ima: any,
  videoElement: HTMLVideoElement,
  videoContainer: HTMLElement,
  appConfig: AppConfig,
  seekPos: number = 0
): AdPlayer => {
  const contentPlaybackDelayMsVal =
    Number(appConfig.contentPlaybackDelayMs) || 500; // midroll updates
  const adToContentTransitionFactorVal =
    Number(appConfig.adToContentTransitionFactor) || 2; // midroll updates
  const adCueTransitionDelayMsVal =
    Number(appConfig.adCueTransitionDelayMs) || 3000; // midroll updates
  const loadVideoTimeout = Number(appConfig.loadVideoTimeout) * 1000 || 8000; // midroll updates
  return (
    createAdsPlayerBuilder()
      .ima(ima)
      .adRequest({
        adTagUrl,
        slotWidth: videoElement.clientWidth,
        slotHeight: videoElement.clientHeight,
      })
      .contentPlayer(playerInstance)
      .contentVideoElement(videoElement) // videoElement on which ad is played
      .contentContainerElement(videoContainer) // videoContainer on which ad UI and settings are rendered
      .deferContentUntilAdLoad(true)
      .imaSmartTvAdPlaybackConfig({
        contentPlaybackDelayMs: contentPlaybackDelayMsVal,
        adToContentTransitionFactor: adToContentTransitionFactorVal,
        adCueTransitionDelayMs: adCueTransitionDelayMsVal,
        contentToAdTransitionDelay: appConfig.contentToAdTransitionDelay
          ? Number(appConfig.contentToAdTransitionDelay)
          : 1000,
      }) // midroll updates
      // .logger(logger)
      .adsRenderingSettings({
        enableUiElements: Boolean(appConfig.enableUiElements),
        useStyledLinearAds: Boolean(appConfig.useStyledLinearAds),
        enablePreloading: Boolean(appConfig.enablePreloading),
        bitrate: appConfig.addPreferredBitrate
          ? Number(appConfig.addPreferredBitrate)
          : 2000, // AHA-7398
        mimeTypes: appConfig.mimeTypes
          ? appConfig.mimeTypes.split(',')
          : ['video/mp4', 'video/webm'],
        loadVideoTimeout,
      })
      .build()
  );
};

export const getAdInfo = (event: AdInfo, adclickURL?: string): any => {
  const addType =
    event.adBreakInfo?.contentTimePosition ||
    (event as any).contentTimePosition;
  return {
    adclickURL: adclickURL || undefined,
    adType: getAdType(addType),
    adDuration: event.durationMs ? event.durationMs / 1000 : 0,
    adskipOffset: event.isSkippable ? event.skipOffsetMs / 1000 : undefined,
  };
};

export const getAdType = (contentTimePosition: string): string => {
  switch (contentTimePosition) {
    case 'unknown':
      return 'Pre';
    case 'midroll':
      return 'Mid';
    case 'postroll':
      return 'Post';
    default:
      return 'Pre';
  }
};
